'use client';
import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '../ui/carousel';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

interface ProductsCarouselProps {
  products: ProductDisplayData[];
  locale?: string;
  query?: FetchProductQuery;
  title: string;
  children?: React.ReactNode;
  className?: {
    section?: string;
    title?: string;
    container?: string;
    slide: string;
  };
}

export function ProductsCarousel({
  products,
  locale,
  query,
  title,
  children,
  className,
}: ProductsCarouselProps) {
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const { data } = useQuery({
    queryKey: ['HOMEPAGE_PRODUCTS', locale, query, me?.customer.id ?? null, me?.authToken ?? null],
    queryFn: () => fetchProducts({ locale: locale!, query, authToken: me?.authToken }),
    enabled: isInitialized && Boolean(me?.authToken) && Boolean(locale) && Boolean(query),
  });
  const displayedProducts = data?.data ?? products;

  return (
    <CarouselProvider>
      <section className={cn('custom-section', 'py-12', className?.section)}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-6 sm:gap-8',
            className?.container,
          )}
        >
          <div className="flex w-full items-center justify-between">
            <h2 className="text-3xl text-foreground sm:text-4xl lg:text-5xl">{title}</h2>
            <div className="flex items-center justify-end gap-2">
              <CarouselControls
                className={{
                  main: 'flex items-center justify-center gap-2',
                }}
              />
              {children && <div className="hidden md:flex">{children}</div>}
            </div>
          </div>
          <Carousel
            className={{
              container: 'h-[480px] w-full gap-4 sm:h-[500px] sm:gap-5',
              main: 'w-full',
              wrapper: 'w-full',
              slide: cn('w-full max-w-[295px]', className?.slide),
            }}
            slides={displayedProducts.map(product => (
              <ProductCard key={product.slug} product={product} imageSizes="295px" deferImage />
            ))}
            options={{
              dragFree: true,
            }}
          />
          {children && <div className="flex w-full md:hidden">{children}</div>}
        </div>
      </section>
    </CarouselProvider>
  );
}
